/**
 * pages/MyEnquiries.jsx
 * ---------------------
 * Callback-history view (route kept at /account/enquiries for backwards
 * compat; v2.71.67 retitled to "My Callbacks" since the enquiry-submission
 * UI was retired in favour of a direct callback flow).
 *
 * v2.71.28 — Initial.
 * v2.71.67 — Retitled to "My Callbacks".
 */

import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { MessageCircle, ArrowRight, ChevronLeft } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import api from "../lib/api";
import { DashboardLayout, RecentItemsList } from "../features/dashboard";
// v2.80 — Re-pull on enquiry mutations (admin marks contacted/closed).
import { useDataSync } from "../lib/dataSync";

function ago(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (isNaN(t)) return "";
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

export default function MyEnquiries() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get("/enquiries/me")
      .then((r) => setItems(r.data?.results || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let cancel = false;
    load();
    return () => { cancel = true; };
  }, [user, load]);

  // v2.80 — Refresh when admin updates the status of any callback.
  useDataSync(["enquiries", "leads"], () => { if (user) load(); });

  if (authLoading) return <DashboardLayout title="My Callbacks" sidebar />;
  if (!user) return <Navigate to="/login?next=/account/enquiries" replace />;

  return (
    <DashboardLayout
      title="My Callbacks"
      subtitle={loading ? "Loading…" : `${items.length} total`}
      actions={
        <Link
          to="/account"
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#71717A] hover:text-[#09090B]"
          data-testid="back-to-account"
        >
          <ChevronLeft size={14} /> Account
        </Link>
      }
    >
      <RecentItemsList
        title={null}
        items={loading ? [] : items}
        emptyHint={loading ? "Loading…" : "No callback requests yet."}
        testid="enquiries-full-list"
        renderItem={(e) => (
          <Link
            to={`/property/${e.property_id}`}
            className="flex items-start gap-3 group"
            data-testid={`full-enquiry-${e.id}`}
          >
            <div className="w-12 h-12 bg-[#F4F4F5] rounded-sm flex-shrink-0 flex items-center justify-center text-[#71717A]">
              <MessageCircle size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-[#09090B] truncate group-hover:text-[#0055FF]">
                {e.property_title || "Property"}
              </div>
              {e.property_area ? (
                <div className="text-[11px] text-[#71717A] mt-0.5">{e.property_area}</div>
              ) : null}
              {e.message ? (
                <div className="text-[12px] text-[#52525B] mt-1 line-clamp-2">{e.message}</div>
              ) : null}
              <div className="text-[11px] mt-1.5 flex items-center gap-2">
                <span className={
                  e.status === "closed" ? "text-[#16A34A] font-medium"
                  : e.status === "contacted" ? "text-[#0055FF] font-medium"
                  : "text-[#A1A1AA] font-medium"
                }>
                  {e.status === "new" ? "Pending" : (e.status || "new").replace("_", " ")}
                </span>
                <span className="text-[#A1A1AA]">· {ago(e.created_at)}</span>
                {e.kind === "callback_request" && (
                  <span className="px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] rounded-sm text-[10px] font-semibold">
                    CALLBACK
                  </span>
                )}
              </div>
            </div>
            <ArrowRight size={14} className="text-[#A1A1AA] group-hover:text-[#0055FF] flex-shrink-0 mt-1" />
          </Link>
        )}
      />
    </DashboardLayout>
  );
}
